import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import styles from '@components/CareProfile.module.css';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import checkboxStyles from '@components/fields/FullWidthCheckboxGroup.module.css';
import Input from '@components/fields/Input';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Form from '@components/forms/Form';
import {
  CareProfileFragment as CareProfile,
  CareProfileCaregiverTypeEnum,
  CareProfileCognitiveStatusEnum,
  CareProfileEmergencyPlanItemEnum,
  CareProfileFallCountEnum,
  CareProfileHelpLevelRequiredEnum,
  CareProfileResidenceTypeEnum,
  CareProfileStabilitySupportEnum,
  CareProfileStageLimitedEnum,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import {
  optionsForBoolean,
  optionsForEmergencyPlanItems,
  optionsForFallCount,
  optionsForResidenceType,
  optionsForStabilitySupport,
  optionsForStageLimited
} from '@utils/optionUtils';

type FormData = {
  caregiverTypes: CareProfileCaregiverTypeEnum[];
  caregiverTypesOther: string | null;
  cognitiveStatuses: CareProfileCognitiveStatusEnum[];
  consideringHousingChange: boolean | null;
  emergencyPlanItems: CareProfileEmergencyPlanItemEnum[];
  fallCount: CareProfileFallCountEnum | null;
  helpLevelRequired: CareProfileHelpLevelRequiredEnum | null;
  id: string;
  residenceType: CareProfileResidenceTypeEnum | null;
  residenceTypeOther: string | null;
  stabilitySupports: CareProfileStabilitySupportEnum[];
  stageBathingAndDressing: CareProfileStageLimitedEnum | null;
  stageContinence: CareProfileStageLimitedEnum | null;
  stageEating: CareProfileStageLimitedEnum | null;
  stageHousekeeping: CareProfileStageLimitedEnum | null;
  stageMealPrep: CareProfileStageLimitedEnum | null;
  stageShopping: CareProfileStageLimitedEnum | null;
  stageToileting: CareProfileStageLimitedEnum | null;
  stageTransportation: CareProfileStageLimitedEnum | null;
  stairsRequiredForExit: boolean | null;
};

type Props = {
  careProfile: CareProfile;
  consultId: string | undefined | null;
  inTracker?: boolean;
  initialConsultReportId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultFunctionalForm = ({
  careProfile,
  consultId,
  initialConsultReportId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('legal');
      } else {
        onSuccess('medical');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const initialValues = {
    caregiverTypes: careProfile?.caregiverTypes?.caregiverTypesOptions ?? [],
    caregiverTypesOther: careProfile.caregiverTypes?.other ?? null,
    cognitiveStatuses: careProfile?.cognitiveStatuses ?? [],
    consideringHousingChange: careProfile.consideringHousingChange ?? null,
    emergencyPlanItems: careProfile?.emergencyPlanItems ?? [],
    fallCount: careProfile?.fallCount ?? null,
    helpLevelRequired: careProfile.helpLevelRequired ?? null,
    residenceType: careProfile.residenceType?.residenceTypeOption ?? null,
    residenceTypeOther: careProfile.residenceType?.other ?? null,
    stabilitySupports: careProfile.stabilitySupports ?? [],
    stageBathingAndDressing: careProfile.stageBathingAndDressing ?? null,
    stageContinence: careProfile.stageContinence ?? null,
    stageEating: careProfile.stageEating ?? null,
    stageHousekeeping: careProfile.stageHousekeeping ?? null,
    stageMealPrep: careProfile.stageMealPrep ?? null,
    stageShopping: careProfile.stageShopping ?? null,
    stageToileting: careProfile.stageToileting ?? null,
    stageTransportation: careProfile.stageTransportation ?? null,
    stairsRequiredForExit: careProfile.stairsRequiredForExit ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);
  const schema = yup
    .object()
    .shape({
      cognitiveStatuses: yup.array(),
      emergencyPlanItems: yup.array(),
      fallCount: yup.string().nullable(),
      helpLevelRequired: yup.string().nullable(),
      residenceType: yup.string().nullable(),
      residenceTypeOther: yup.string().nullable(),
      stabilitySupports: yup.array(),
      stageBathingAndDressing: yup.string().nullable(),
      stageContinence: yup.string().nullable(),
      stageEating: yup.string().nullable(),
      stageHousekeeping: yup.string().nullable(),
      stageMealPrep: yup.string().nullable(),
      stageShopping: yup.string().nullable(),
      stageToileting: yup.string().nullable(),
      stageTransportation: yup.string().nullable(),
      stairsRequiredForExit: yup.boolean().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const residenceType = form.watch('residenceType');
  const residenceTypeOther = form.watch('residenceTypeOther');
  const caregiverTypes = form.watch('caregiverTypes');

  useEffect(() => {
    if (residenceTypeOther && residenceTypeOther !== '') {
      form.setValue('residenceType', CareProfileResidenceTypeEnum.other);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceTypeOther]);

  useEffect(() => {
    if (residenceType !== CareProfileResidenceTypeEnum.other) {
      form.setValue('residenceTypeOther', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceType]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        caregiverTypes: {
          caregiverTypesOptions: form.getValues().caregiverTypes,
          other: form.getValues().caregiverTypes.includes(CareProfileCaregiverTypeEnum.other)
            ? form.getValues().caregiverTypesOther
            : null
        },
        consultId,
        id: initialConsultReportId,
        residenceType: {
          other: form.getValues().residenceTypeOther,
          residenceTypeOption: form.getValues().residenceType
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (!questions) {
            setSubmitted(true);
          } else {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [consultId, form, initialConsultReportId, intl, onSuccess, questions, updateInitialConsultReport]);

  const stabilitySupportOptions = useMemo(() => optionsForStabilitySupport(intl), [intl]);

  const fallCountOptions = useMemo(() => optionsForFallCount(intl), [intl]);

  const helpLevelRequiredOptions = useMemo(() => {
    const options = [
      { option: CareProfileHelpLevelRequiredEnum.independent },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.help_level_required.options.basic_support.help' }),
        option: CareProfileHelpLevelRequiredEnum.basic_support
      },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.help_level_required.options.constant_care.help' }),
        option: CareProfileHelpLevelRequiredEnum.constant_care
      },
      { option: CareProfileHelpLevelRequiredEnum.crisis },
      { option: CareProfileHelpLevelRequiredEnum.end_of_life },
      { option: CareProfileHelpLevelRequiredEnum.deceased }
    ];
    return options.map(option => ({
      help: option.help,
      label: intl.formatMessage({
        id: `models.pme.questions.help_level_required.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const caregiverTypeOptions = useMemo(() => {
    const options = [
      CareProfileCaregiverTypeEnum.children,
      CareProfileCaregiverTypeEnum.spouse,
      CareProfileCaregiverTypeEnum.siblings,
      CareProfileCaregiverTypeEnum.friends,
      CareProfileCaregiverTypeEnum.caregivers,
      CareProfileCaregiverTypeEnum.neighbors,
      CareProfileCaregiverTypeEnum.other
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.caregiver_types.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  const emergencyPlanItemOptions = useMemo(() => optionsForEmergencyPlanItems(intl), [intl]);

  const cognitiveStatusOptions = useMemo(() => {
    const options = [
      CareProfileCognitiveStatusEnum.none,
      CareProfileCognitiveStatusEnum.forgetful,
      CareProfileCognitiveStatusEnum.short_term_memory_loss,
      CareProfileCognitiveStatusEnum.long_term_memory_loss
    ];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.safety.questions.cognitive_statuses.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const residenceTypeOptions = useMemo(() => optionsForResidenceType(intl), [intl]);

  const stageLimitedOptions = useMemo(() => optionsForStageLimited(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={!questions ? undefined : 'ion-hide'}>
        <div className="ion-margin-top">
          <RadioButtonGroup<FormData>
            allowDeselection
            control={form.control}
            error={form.formState.errors.residenceType}
            inTracker={false}
            label={intl.formatMessage({ id: 'models.pme.questions.residence_type.content' })}
            name="residenceType"
            options={residenceTypeOptions}
          />
        </div>
        <div className={styles.otherWrapper}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.residenceTypeOther}
            isOption
            name="residenceTypeOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.residence_type.options.other.content'
            })}
            spellcheck
          />
        </div>
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.CONSIDERING_HOUSING_CHANGE) ? undefined : 'ion-hide'
        }
      >
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.consideringHousingChange}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.careProfile.questions.considering_housing_change.content' })}
          name="consideringHousingChange"
          options={booleanOptions}
        />
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.CAREGIVER_TYPES) ? undefined : 'ion-hide'}>
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.caregiverTypes}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.caregiver_types.content' })}
          name="caregiverTypes"
          options={caregiverTypeOptions}
          smallerLabel
        />
        <div className={caregiverTypes.includes(CareProfileCaregiverTypeEnum.other) ? undefined : 'ion-hide'}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.caregiverTypesOther}
            label={intl.formatMessage({
              id: 'models.pme.questions.residence_type.options.other.content'
            })}
            name="caregiverTypesOther"
            spellcheck
          />
        </div>
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <CheckboxGroup
          control={form.control}
          errors={form.formState.errors.cognitiveStatuses}
          label={intl.formatMessage({ id: 'models.safety.questions.cognitive_statuses.content' })}
          name="cognitiveStatuses"
          options={cognitiveStatusOptions}
          smallerLabel
        />
        <CheckboxGroup
          control={form.control}
          errors={form.formState.errors.stabilitySupports}
          label={intl.formatMessage({ id: 'models.safety.questions.stability_supports.content' })}
          name="stabilitySupports"
          options={stabilitySupportOptions}
          smallerLabel
        />
        <RadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.fallCount}
          label={intl.formatMessage({ id: 'models.safety.questions.fall_count.content' })}
          name="fallCount"
          options={fallCountOptions}
        />
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.stairsRequiredForExit}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.safety.questions.stairs_required_for_exit.content' })}
          name="stairsRequiredForExit"
          options={booleanOptions}
        />
        <CheckboxGroup
          control={form.control}
          errors={form.formState.errors.emergencyPlanItems}
          label={intl.formatMessage({ id: 'models.safety.questions.emergency_plan_items.content' })}
          name="emergencyPlanItems"
          options={emergencyPlanItemOptions}
          smallerLabel
        />
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.STAGES) ? undefined : 'ion-hide'}>
        <div className={checkboxStyles.formTitle}>
          <FormattedMessage id="models.initialConsultReport.questions.stages.content" />
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.bathing_and_dressing" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageBathingAndDressing}
              name="stageBathingAndDressing"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.continence" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageContinence}
              name="stageContinence"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.eating" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageEating}
              name="stageEating"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.housekeeping" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageHousekeeping}
              name="stageHousekeeping"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.meal_prep" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageMealPrep}
              name="stageMealPrep"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.shopping" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageShopping}
              name="stageShopping"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.toileting" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageToileting}
              name="stageToileting"
              options={stageLimitedOptions}
            />
          </div>
        </div>
        <div className={checkboxStyles.stage}>
          <div className={checkboxStyles.title}>
            <FormattedMessage id="models.initialConsultReport.questions.stages.types.transportation" />
          </div>
          <div className={checkboxStyles.buttonGroup}>
            <RadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.stageTransportation}
              name="stageTransportation"
              options={stageLimitedOptions}
            />
          </div>
        </div>
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <div className="ion-margin-top">
          <RadioButtonGroup<FormData>
            allowDeselection
            control={form.control}
            error={form.formState.errors.helpLevelRequired}
            inTracker={false}
            label={intl.formatMessage({ id: 'models.pme.questions.help_level_required.content' })}
            name="helpLevelRequired"
            options={helpLevelRequiredOptions}
          />
        </div>
      </div>
      {!questions ? (
        <div className={styles.spaceBetween}>
          <SecondaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(false)}
            textKey="dictionary.previous"
            type="submit"
          />
          <PrimaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(true)}
            textKey="dictionary.next"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default InitialConsultFunctionalForm;
